import React, { useState, useEffect } from 'react';
import '../styles/learn.css';

import { CodeEditor, Quiz } from './Activities.jsx';
import QuizScoreModal from '../components/QuizScoreModal';
import { submitQuiz, generateSectionTest } from '../context/engine.ts';
import { supabase } from '../integrations/client.ts';
import supabaseLocalData from '../fake_data/supabase-local.json';

function CourseHandsOn({ selectedSection, currentUser, currentCourse, courseData, onSectionSelect, onCloseHandsOn, onProgressUpdate }) {
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [quizResult, setQuizResult] = useState(null);
    const [currentTest, setCurrentTest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generatingTest, setGeneratingTest] = useState(false);
    const [userProgress, setUserProgress] = useState(null);
    const [quizResetKey, setQuizResetKey] = useState(0); // Key to force Quiz component reset

    // Fetch user progress for the current section
    useEffect(() => {
        const fetchUserProgress = async () => {
            if (!selectedSection?.id || !currentUser?.id || !currentCourse?.id) {
                setUserProgress(null);
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('user_progress')
                    .select('section_id, unlocked, quiz_completed, quiz_score')
                    .eq('user_id', currentUser.id)
                    .eq('course_id', currentCourse.id)
                    .eq('section_id', selectedSection.id)
                    .single();

                if (!error && data) {
                    // Calculate passed status from score (60% or higher)
                    const passed = data.quiz_score >= 60;
                    setUserProgress({ ...data, quiz_passed: passed });
                } else {
                    setUserProgress(null);
                }
            } catch (err) {
                setUserProgress(null);
            }
        };

        fetchUserProgress();
    }, [selectedSection, currentUser, currentCourse]);

    // Check if test exists when section changes (but don't generate it yet)
    useEffect(() => {
        const checkExistingTest = async () => {
            if (!selectedSection?.id) {
                setCurrentTest(null);
                return;
            }

            try {
                // Check if test already exists AND has questions
                const { data: testData, error: testError } = await supabase
                    .from('tests')
                    .select('id')
                    .eq('section_id', selectedSection.id)
                    .limit(1);

                if (!testError && testData && testData.length > 0) {
                    // Test exists, now verify it has questions
                    const { data: questionsData, error: questionsError } = await supabase
                        .from('questions')
                        .select('id')
                        .eq('test_id', testData[0].id)
                        .limit(1);

                    if (!questionsError && questionsData && questionsData.length > 0) {
                        // Test has questions, load it
                        setCurrentTest(testData[0]);
                        return;
                    }
                }

                // No test found - will be generated when user clicks "Show Practice"
                setCurrentTest(null);
            } catch (err) {
                setCurrentTest(null);
            }
        };

        checkExistingTest();
    }, [selectedSection]);

    // Generate test on demand when practice view is accessed for the first time (PREMIUM ONLY)
    useEffect(() => {
        const generateTestOnDemand = async () => {
            // Only generate if:
            // 1. Section is selected
            // 2. User is authenticated
            // 3. No test exists yet
            // 4. Not already generating
            // 5. User is premium
            if (!selectedSection?.id || !currentCourse || !currentUser || currentTest || generatingTest) {
                return;
            }

            // Check if user is premium - only premium users can GENERATE new tests
            const isPremium = currentUser.subscription_type === 'premium';
            if (!isPremium) {
                return;
            }

            try {
                setGeneratingTest(true);

                // Double-check test doesn't exist (race condition prevention)
                const { data: existingTest } = await supabase
                    .from('tests')
                    .select('id')
                    .eq('section_id', selectedSection.id)
                    .limit(1);

                if (existingTest && existingTest.length > 0) {
                    setCurrentTest(existingTest[0]);
                    setGeneratingTest(false);
                    return;
                }

                // Get section content
                const { data: sectionData, error: sectionError } = await supabase
                    .from('sections')
                    .select('content')
                    .eq('id', selectedSection.id)
                    .single();

                if (sectionError || !sectionData?.content) {
                    setGeneratingTest(false);
                    return;
                }

                // Generate and save test to Supabase
                const result = await generateSectionTest(
                    selectedSection.id,
                    currentCourse.name,
                    selectedSection.chapter_name || 'Chapter',
                    selectedSection.name,
                    sectionData.content
                );

                setCurrentTest({ id: result.testId });
                setGeneratingTest(false);
            } catch (err) {
                setGeneratingTest(false);
            }
        };

        // Trigger generation when component is visible and conditions are met
        generateTestOnDemand();
    }, [selectedSection, currentCourse, currentUser, currentTest, generatingTest]);

    const handleQuizSubmit = async (answers) => {
        if (!currentUser || !currentCourse || !selectedSection) {
            alert('Please sign in and select a course to submit the quiz');
            return;
        }

        setLoading(true);

        try {
            const result = await submitQuiz(
                currentUser.id,
                currentCourse.id,
                selectedSection.id,
                answers
            );

            const correctAnswers = Math.round((result.score / 100) * result.totalQuestions);

            setQuizResult({
                ...result,
                correctAnswers,
                incorrectQuestions: result.incorrectQuestions || []
            });
            setShowScoreModal(true);

            // Refresh user progress after submitting
            const { data } = await supabase
                .from('user_progress')
                .select('section_id, unlocked, quiz_completed, quiz_score')
                .eq('user_id', currentUser.id)
                .eq('course_id', currentCourse.id)
                .eq('section_id', selectedSection.id)
                .single();

            if (data) {
                const passed = data.quiz_score >= 60;
                setUserProgress({ ...data, quiz_passed: passed });
            }

            // Trigger progress refresh in Chapters component
            if (onProgressUpdate) {
                onProgressUpdate();
            } else {
            }
        } catch (error) {
            alert('Failed to submit quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleShowResults = () => {
        if (!userProgress || !userProgress.quiz_completed) {
            return;
        }

        // Calculate correct answers based on score percentage
        const totalQuestions = 5; // Tests have 5 questions
        const correctAnswers = Math.round((userProgress.quiz_score / 100) * totalQuestions);

        // Note: When viewing past results, we don't have access to incorrect questions
        // This is intentional - we only show incorrect questions immediately after taking the quiz
        setQuizResult({
            score: userProgress.quiz_score,
            passed: userProgress.quiz_passed,
            totalQuestions,
            correctAnswers,
            incorrectQuestions: [] // Not available for past results
        });
        setShowScoreModal(true);
    };

    const handleContinue = async () => {
        setShowScoreModal(false);

        // Close the practice panel
        if (onCloseHandsOn) {
            onCloseHandsOn();
        }

        // Small delay to allow Chapters component to refresh progress
        await new Promise(resolve => setTimeout(resolve, 100));

        // Get next section info
        const { nextSection } = getNextSectionInfo();

        if (nextSection && onSectionSelect) {
            // Navigate to the next section
            onSectionSelect({
                ...nextSection,
                chapter_name: nextSection.chapter_name,
                chapter_id: nextSection.chapter_id
            });
        }
    };

    const handleReview = () => {
        setShowScoreModal(false);
        // Close hands-on panel to review content
    };

    const handleTryAgain = async () => {
        setShowScoreModal(false);

        // Reset quiz completion in the database to allow retaking
        if (currentUser && currentCourse && selectedSection) {
            try {
                await supabase
                    .from('user_progress')
                    .update({
                        quiz_completed: false,
                        quiz_score: 0
                    })
                    .eq('user_id', currentUser.id)
                    .eq('course_id', currentCourse.id)
                    .eq('section_id', selectedSection.id);

                // Update local state to reflect the reset
                setUserProgress({
                    ...userProgress,
                    quiz_completed: false,
                    quiz_score: 0,
                    quiz_passed: false
                });

                // Force Quiz component to reset by changing its key
                setQuizResetKey(prev => prev + 1);
            } catch (error) {
                console.error('Failed to reset quiz:', error);
            }
        }
    };

    // Get next section information
    const getNextSectionInfo = () => {
        if (!selectedSection || !courseData || !Array.isArray(courseData.chapters)) {
            return { nextSection: null, isNewChapter: false, isLastSectionInChapter: false };
        }

        // Find current chapter
        const currentChapter = courseData.chapters.find(ch => ch.id === selectedSection.chapter_id);

        // Check if this is the last section in the current chapter
        let isLastSectionInChapter = false;
        if (currentChapter && currentChapter.sections) {
            const currentSectionIndex = currentChapter.sections.findIndex(s => s.id === selectedSection.id);
            isLastSectionInChapter = currentSectionIndex === currentChapter.sections.length - 1;
        }

        // Flatten all sections across all chapters
        const allSections = [];
        courseData.chapters.forEach(chapter => {
            if (chapter.sections && Array.isArray(chapter.sections)) {
                chapter.sections.forEach(section => {
                    allSections.push({
                        ...section,
                        chapter_name: chapter.name,
                        chapter_id: chapter.id
                    });
                });
            }
        });

        // Find current section index
        const currentIndex = allSections.findIndex(s => s.id === selectedSection.id);

        if (currentIndex === -1 || currentIndex === allSections.length - 1) {
            // Not found or is last section
            return { nextSection: null, isNewChapter: false, isLastSectionInChapter };
        }

        const nextSection = allSections[currentIndex + 1];
        const currentChapterId = selectedSection.chapter_id;
        const isNewChapter = nextSection.chapter_id !== currentChapterId;

        return { nextSection, isNewChapter, isLastSectionInChapter };
    };

    const renderActivityContent = () => {
        if (!selectedSection) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#666',
                    fontSize: '16px'
                }}>
                    <h3>Practice Mode</h3>
                    <p>Select a section from the chapters to access practice activities.</p>
                    <p style={{ fontSize: '14px', fontStyle: 'italic' }}>
                        Practice activities are tailored to each section's learning objectives.
                    </p>
                </div>
            );
        }

        // Check if test is being generated
        if (generatingTest) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#666',
                    fontSize: '16px'
                }}>
                    <h3 style={{ color: '#2196f3', marginBottom: '12px' }}>Generating Quiz</h3>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                        Creating practice questions for: <strong>{selectedSection.name}</strong>
                    </p>
                    <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#999' }}>
                        This may take 10-20 seconds...
                    </p>
                    <div style={{
                        marginTop: '24px',
                        padding: '12px 20px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#1976d2'
                    }}>
                        Your quiz will be saved and ready for future practice sessions!
                    </div>
                </div>
            );
        }

        // Check if there's a test available for this section
        if (!currentTest) {
            const isPremium = currentUser?.subscription_type === 'premium';

            return (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#666',
                    fontSize: '16px'
                }}>
                    <h3>Practice: {selectedSection.name}</h3>
                    {isPremium ? (
                        <>
                            <p style={{ marginTop: '16px', fontSize: '15px' }}>
                                Quiz is being prepared for this section...
                            </p>
                            <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#999' }}>
                                Please wait a moment while we generate your practice quiz.
                            </p>
                        </>
                    ) : (
                        <>
                            <p style={{ marginTop: '16px' }}>
                                No practice quiz available yet for this section.
                            </p>
                            <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#999' }}>
                                Practice quizzes are generated when a Premium user completes this section, or when our team uploads the course. Check back later!
                            </p>
                        </>
                    )}
                </div>
            );
        }

        // Check if quiz has already been completed
        const isQuizCompleted = userProgress?.quiz_completed;

        // If there's a test, show the quiz or results button
        return (
            <div>
                {isQuizCompleted ? (
                    // Show "See Results" button for completed quizzes
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px'
                    }}>
                        <p style={{
                            marginBottom: '24px',
                            fontSize: '16px',
                            color: '#666'
                        }}>
                            You've already completed this quiz with a score of <strong>{Math.round(userProgress.quiz_score)}%</strong>.
                        </p>
                        <button
                            onClick={handleShowResults}
                            style={{
                                padding: '12px 32px',
                                fontSize: '16px',
                                fontWeight: '500',
                                backgroundColor: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#1976d2';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#2196f3';
                            }}
                        >
                            See Results
                        </button>
                    </div>
                ) : (
                    // Show the quiz for first-time takers
                    <>
                        <div className="activity-component">
                            <Quiz
                                key={quizResetKey}
                                testId={currentTest.id}
                                sectionId={selectedSection.id}
                                onSubmit={handleQuizSubmit}
                                allowSubmit={!loading}
                            />
                        </div>

                        {loading && (
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#666',
                                fontSize: '14px'
                            }}>
                                Submitting your quiz...
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="hands-on-container">
            {/* Mobile-only header with back button */}
            <div className="hands-on-mobile-header">
                <button
                    className="back-to-content-btn-mobile"
                    onClick={() => {
                        if (onCloseHandsOn) {
                            onCloseHandsOn();
                        }
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        padding: '8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    ← Back to learning material
                </button>
            </div>
            <div className="practice-area">
                {renderActivityContent()}
            </div>

            {/* Quiz Score Modal */}
            {showScoreModal && quizResult && (() => {
                const { nextSection, isNewChapter, isLastSectionInChapter } = getNextSectionInfo();
                const isLastSection = nextSection === null;

                // Show chapter project message if passed quiz, is last section in chapter, AND course is skill-based
                const showChapterProjectMessage = quizResult.passed &&
                                                 isLastSectionInChapter &&
                                                 currentCourse?.is_skill_based;

                return (
                    <QuizScoreModal
                        score={quizResult.score}
                        passed={quizResult.passed}
                        totalQuestions={quizResult.totalQuestions}
                        correctAnswers={quizResult.correctAnswers}
                        incorrectQuestions={quizResult.incorrectQuestions}
                        isLastSection={isLastSection}
                        nextSectionName={nextSection?.name}
                        nextChapterName={isNewChapter ? nextSection?.chapter_name : null}
                        recommendedCourse={currentCourse?.recommended_next_course}
                        showChapterProjectMessage={showChapterProjectMessage}
                        currentChapterName={selectedSection?.chapter_name}
                        sectionId={selectedSection?.id}
                        onContinue={handleContinue}
                        onReview={handleReview}
                        onClose={quizResult.passed ? () => setShowScoreModal(false) : handleTryAgain}
                    />
                );
            })()}
        </div>
    );
}

export default CourseHandsOn;